import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { flexRender, type Header, type SortingFn } from "@tanstack/react-table";

import { TableHead } from "../../ui/table";
import { cn } from "../../utils/common/cn";
import { isBreached, severityRank } from "./format";
import type { ConcentrationRow } from "../../utils/portfolio-risk-concentration/api/types";

/**
 * Click-to-sort headers for the Monitor and Capacity tables.
 *
 * Both tables already built a TanStack table with `getSortedRowModel`, but nothing ever
 * triggered it: the rows arrived pre-sorted by severity and the headers were inert text.
 * Two problems with that, and this fixes both.
 *
 * **The default order is now Category.** Severity-first answered "what is on fire", which the
 * banner above the table already answers by name — and it scattered a category's limits down
 * the table, so the one question the table itself is good at ("what does the book look like
 * across Equipment") needed the reader to scan every row. Severity is still one click away on
 * the Status header.
 *
 * **Categorical columns sort by MEANING, not alphabetically.** Status ascending is
 * RED → AMBER → GREEN, not AMBER → GREEN → RED, which is what a string sort would give and
 * would be useless. Same for Direction. Text and numeric columns sort the ordinary way.
 */

/** Worst first when ascending. A string sort on the badge text is meaningless. */
export const statusSortingFn: SortingFn<ConcentrationRow> = (a, b) =>
  severityRank(a.original.status) - severityRank(b.original.status);

/**
 * Most concerning first when ascending.
 *
 * Breached outranks every movement value, because that is what the column now displays for
 * a breached limit — sorting on the underlying `direction` would scatter the Breached rows
 * through the list according to a word nobody can see.
 */
const DIRECTION_RANK: Record<ConcentrationRow["direction"], number> = {
  APPROACHING: 1,
  STABLE: 2,
  RECEDING: 3,
};
const directionRank = (r: ConcentrationRow): number =>
  isBreached(r) ? 0 : DIRECTION_RANK[r.direction];
export const directionSortingFn: SortingFn<ConcentrationRow> = (a, b) =>
  directionRank(a.original) - directionRank(b.original);

/**
 * A sparkline has no single value to sort on, so this sorts by what the picture actually
 * shows: how far the series moved over the window, last point minus first. Ascending puts
 * the steepest FALLS first and the steepest climbs last.
 *
 * Exported because TanStack refuses to sort a column with no accessor at all
 * (`getCanSort` requires `!!column.accessorFn`), so the Trend column needs this as its
 * accessorFn as well as its comparator — a display-only column would render a sort arrow
 * that does nothing.
 */
export const trendDelta = (r: ConcentrationRow): number => {
  const t = r.trend;
  if (!t || t.length < 2) return 0;
  return (t[t.length - 1]?.dollarPct ?? 0) - (t[0]?.dollarPct ?? 0);
};
export const trendSortingFn: SortingFn<ConcentrationRow> = (a, b) =>
  trendDelta(a.original) - trendDelta(b.original);

export function SortableTableHead<T>({ header }: { header: Header<T, unknown> }) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const label = header.isPlaceholder
    ? null
    : flexRender(header.column.columnDef.header, header.getContext());

  if (!canSort) {
    return (
      <TableHead className="text-[11px] uppercase tracking-[0.12em]">{label}</TableHead>
    );
  }

  return (
    <TableHead className="text-[11px] uppercase tracking-[0.12em]">
      <button
        type="button"
        onClick={header.column.getToggleSortingHandler()}
        // aria-sort belongs on the cell, but the cell is not the control; announcing the
        // state on the button is what a screen reader actually reaches here.
        aria-label={`Sort by ${header.column.id}${
          sorted === "asc" ? ", currently ascending" : sorted === "desc" ? ", currently descending" : ""
        }`}
        className={cn(
          "-mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 uppercase tracking-[0.12em]",
          "transition-colors hover:text-foreground",
          sorted ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
        {/* The neutral glyph is always rendered rather than appearing on hover: a sort
            affordance nobody can see is one nobody uses, and it also stops the header
            shifting by an icon's width on hover. */}
        {sorted === "asc" ? (
          <ChevronUp className="h-3 w-3 shrink-0" />
        ) : sorted === "desc" ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}
